import * as React from 'react';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { BoxesSliderSlideSize, Breakpoint, Color, DirectionEnum, IconName } from '../../enums';
import { useAfterHydration } from '../../hooks';
import { BoxSlide } from '../../interfaces';
import { UseState } from '../../types';
import { BoxTitle } from '../boxTitle/boxTitle.component';
import { Icon } from '../icon/icon.component';

import {
  Container,
  SlideItem,
  SlideImage,
  SlideDetails,
  ArrowButton,
  SlideItemWrapper,
  SlideBadge
} from './boxesSlider.styled';
import { BoxesSliderProps } from './boxesSlider.types';

export const BoxesSlider: React.FC<BoxesSliderProps> = (props: BoxesSliderProps) => {
  const { slides, slideSize = BoxesSliderSlideSize.Small, className }: BoxesSliderProps = props;
  const [swiperInstance, setSwiperInstance]: UseState<SwiperType | null> = useState<SwiperType | null>(null);
  const [isAtEnd, setIsAtEnd]: UseState<boolean> = useState<boolean>(true);
  const [isAtBeginning, setIsAtBeginning]: UseState<boolean> = useState<boolean>(true);
  const isSmallMobile: boolean = useMediaQuery({ query: Breakpoint.SmallMobile });
  /**
   * `$size` wchodzi do klas styled-components, a serwer nie zna szerokości ekranu, więc
   * renderuje rozmiar przekazany propsem. Przy podłączaniu React zostawia atrybuty
   * z serwera i NIE poprawia klasy, więc wejście z adresu na telefonie zostawało
   * z rozmiarem desktopowym: kafel 515 px na ekranie 380 px. Stąd `useAfterHydration`,
   * które czeka tylko przy podłączaniu, a późniejszym montowaniom oddaje odpowiedź od razu.
   */
  const poPodlaczeniu: boolean = useAfterHydration();
  const resolvedSlideSize: BoxesSliderSlideSize = poPodlaczeniu && isSmallMobile
    ? BoxesSliderSlideSize.Small
    : slideSize;

  const onSlideChange: (swiper: SwiperType) => void = (swiper: SwiperType): void => {
    setIsAtEnd(swiper.isEnd);
    setIsAtBeginning(swiper.isBeginning);
  };

  const handleSwiperControlsVisibleState: () => void = (): void => {
    if (swiperInstance) {
      setIsAtBeginning(swiperInstance.isBeginning || !swiperInstance.allowSlidePrev);
      setIsAtEnd(swiperInstance.isEnd || !swiperInstance.allowSlideNext);
    }
  };

  useEffect(
    (): void => handleSwiperControlsVisibleState(),
    [swiperInstance, slides.length, swiperInstance?.allowSlideNext, swiperInstance?.allowSlidePrev]
  );

  useEffect(
    (): () => void => {
      window.addEventListener('resize', handleSwiperControlsVisibleState);

      return (): void => window.removeEventListener('resize', handleSwiperControlsVisibleState);
    },
    [swiperInstance]
  );

  return (
    <Container className={className}>
      {swiperInstance && (
        <ArrowButton
          onClick={() => swiperInstance.slidePrev()}
          $position={DirectionEnum.Left}
          $visible={!isAtBeginning}
          $size={resolvedSlideSize}
        >
          <Icon
            name={IconName.ArrowLeft25}
            size={21}
            color={Color.Gray500}
          />
        </ArrowButton>
      )}
      <Swiper
        spaceBetween={10}
        slidesPerView='auto'
        onSlideChange={onSlideChange}
        onSwiper={setSwiperInstance}
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        {slides.map((slide: BoxSlide, index: number): JSX.Element => (
          <SwiperSlide style={{ width: 'auto' }} key={`${slide.boxTitleDetails.author ?? ''}_${index}`}>
            <SlideItem $isFirst={index === 0}>
              <SlideItemWrapper {...slide}>
                {!!slide.badge && (
                  <SlideBadge 
                    label={slide.badge.label} 
                    color={slide.badge.color} 
                    background={slide.badge.background} 
                    $isFirst={index === 0} 
                  />
                )}
                <SlideImage
                  src={slide.image}
                  alt={slide.imageAlt ?? ''}
                  $size={resolvedSlideSize}
                />
                <SlideDetails>
                  <BoxTitle boxTitleDetails={slide.boxTitleDetails} />
                </SlideDetails>
              </SlideItemWrapper>
            </SlideItem>
          </SwiperSlide>
        ))}        
      </Swiper>
      {swiperInstance && (
        <ArrowButton
          onClick={() => swiperInstance.slideNext()}
          $position={DirectionEnum.Right}
          $visible={!isAtEnd}
          $size={resolvedSlideSize}
        >
          <Icon
            name={IconName.ArrowRight25}
            size={21}
            color={Color.Gray500}
          />
        </ArrowButton>
      )}
    </Container>
  );
};
